import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Returns true when the query matches.
 *
 * Example:
 *   const isMobile = useMediaQuery('(max-width: 767px)');
 *
 * Safe under SSR (returns false on the server) and React StrictMode (cleans
 * up the listener on unmount).
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    // Sync once on mount in case the query state changed between initial
    // render and effect commit (e.g. orientation change during hydration).
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
